import type { UploadProgress } from "./data-source.types";

// Graph wants every chunk but the last to be a multiple of 320 KiB. 10 MiB keeps
// a 2 GB video to about 200 requests without holding much of it in memory.
export const CHUNK_BYTES = 32 * 320 * 1024;
const ATTEMPTS = 3;

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** One chunk, retried when the network or SharePoint stumbles rather than refuses. */
async function sendChunk(uploadUrl: string, file: Blob, start: number, end: number) {
  for (let attempt = 1; ; attempt++) {
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Range": `bytes ${start}-${end - 1}/${file.size}` },
      body: file.slice(start, end),
    }).catch(() => null);
    if (response?.ok) return;
    const retryable = !response || response.status >= 500 || response.status === 429;
    if (!retryable || attempt === ATTEMPTS) {
      throw new Error("The upload to SharePoint failed. Try again.");
    }
    await wait(attempt * 1000);
  }
}

/**
 * Sends a file to a SharePoint upload session in order, chunk by chunk. The
 * session URL carries its own authorisation, for this one file, so no token
 * goes with it.
 */
export async function uploadToSession(
  uploadUrl: string,
  file: Blob,
  onProgress?: UploadProgress
): Promise<void> {
  for (let start = 0; start < file.size; start += CHUNK_BYTES) {
    const end = Math.min(start + CHUNK_BYTES, file.size);
    await sendChunk(uploadUrl, file, start, end);
    onProgress?.(end / file.size);
  }
}
